import { useId, type ReactNode, type ButtonHTMLAttributes } from 'react';
import { AlertCircle, CheckCircle2, Info, Loader2 } from 'lucide-react';
import './organization-settings.css';
import { SettingsHelp } from './SettingsHelp';
export { SettingsHelp } from './SettingsHelp';

/** Shared, scoped presentation for organization preferences and warehouse actions. */
export function SettingsSection({ title, description, children }: {
  title: string; description?: string; children: ReactNode;
}) {
  const id = useId();
  return <section className="org-settings-section" aria-labelledby={id}>
    <header className="org-settings-section-heading">
      <h3 id={id}>{title}</h3>
      {description && <p>{description}</p>}
    </header>
    <div className="org-settings-section-content">{children}</div>
  </section>;
}

export function SettingsField({ label, hint, help, children }: {
  label: string; hint?: ReactNode; help?: ReactNode; children: (id: string, hintId?: string) => ReactNode;
}) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  return <div className="org-settings-field">
    <div className="org-settings-field-label">
      <label htmlFor={id}>{label}</label>
      {(help || hint) && <SettingsHelp label={label}>{help || hint}</SettingsHelp>}
      {hint && <p id={hintId}>{hint}</p>}
    </div>
    <div className="org-settings-field-control">{children(id, hintId)}</div>
  </div>;
}

export function SettingsToggle({ label, description, help, checked, onChange, disabled }: {
  label: string; description?: string; help?: ReactNode; checked: boolean;
  onChange: (checked: boolean) => void; disabled?: boolean;
}) {
  const id = useId();
  return <div className="org-settings-toggle-row">
    <div>
      <label htmlFor={id}>{label}</label>
      {(help || description) && <SettingsHelp label={label}>{help || description}</SettingsHelp>}
      {description && <p id={`${id}-hint`}>{description}</p>}
    </div>
    <input id={id} className="org-settings-switch" type="checkbox" role="switch"
      checked={checked} disabled={disabled}
      aria-describedby={description ? `${id}-hint` : undefined}
      onChange={event => onChange(event.target.checked)} />
  </div>;
}

export type SettingsFeedbackValue = { text: string; tone: 'success' | 'error' | 'info' } | null;
export function SettingsFeedback({ value }: { value: SettingsFeedbackValue }) {
  const Icon = value?.tone === 'error' ? AlertCircle : value?.tone === 'success' ? CheckCircle2 : Info;
  return <div className={`org-settings-feedback ${value ? `is-${value.tone}` : ''}`}
    role={value?.tone === 'error' ? 'alert' : 'status'} aria-live="polite" aria-atomic="true">
    {value && <><Icon size={16} aria-hidden="true" /><span>{value.text}</span></>}
  </div>;
}

export function SettingsButton({ variant = 'secondary', busy, children, ...props }:
  ButtonHTMLAttributes<HTMLButtonElement> & { variant?: 'primary' | 'secondary' | 'ghost' | 'danger'; busy?: boolean }) {
  return <button {...props} type={props.type || 'button'} aria-busy={busy || undefined}
    className={`org-settings-button is-${variant} ${props.className || ''}`}>
    {busy && <Loader2 size={15} className="animate-spin" aria-hidden="true" />}{children}
  </button>;
}
